/**
 * The external dependencies
 */

/** @jsxRuntime classic */
/** @jsx jsx */
import { css, jsx } from '@emotion/react';
import { Box, Icon, Tooltip, Typography } from '@mui/material';
import { observer } from 'mobx-react-lite';

import {
  ListChangeDto,
  UpdateChangeDtoStatusEnum,
} from '@codefluegel/zeta-change-typescript-client';
import {
  DataGrid,
  GridColDef,
  GridToolbarContainer,
  GridToolbarQuickFilter,
} from '@mui/x-data-grid';

import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { formatNumber } from '../../utils/getTotalTime';
import { useProjectStore } from '../../store/projectStore';
import TaskCardIconMap from '../../utils/changes';
import { getChangeLinkByStatus } from '../../utils/utils';
import { TaskCardLocalizationList } from '../../constants/changes';

type DataGridProps = {
  changeRequests: ListChangeDto[];
};

const ChangeDataGrid = observer(({ changeRequests }: DataGridProps) => {
  const { t } = useTranslation();

  const navigation = useNavigate();
  const TaskCardLocalization = TaskCardLocalizationList();
  const { projects } = useProjectStore();

  const columns: GridColDef[] = [
    { field: 'changeNo', headerName: t('change.ChangeNo'), width: 150 },
    { field: 'title', headerName: t('change.title'), width: 200 },
    { field: 'initiator', headerName: t('change.initiator'), width: 150 },
    {
      field: 'changeNoClient',
      headerName: t('change.changeNoClient'),
      width: 150,
    },
    {
      field: 'projectCode',
      headerName: t('project.projectShortCut'),
      width: 120,
      renderCell: cellValues => (
        <Typography>
          {projects.find((proj: any) => proj.id === cellValues.row.projectId)
            ?.code || '-'}
        </Typography>
      ),
    },
    { field: 'statusClient', headerName: t('status.clientStatus'), width: 100 },
    {
      field: 'createdAt',
      headerName: t('dates.createdat'),
      width: 100,
      renderCell: cellValues => (
        <Typography>
          {cellValues.row.createdAt
            ? new Date(cellValues.row.createdAt).toLocaleDateString('de-DE')
            : '-'}
        </Typography>
      ),
    },
    {
      field: 'statusText',
      headerName: t('status.status'),
      width: 250,
      renderCell: cellValues => (
        <Tooltip
          title={t(cellValues.row.statusText).toString()}
          placement="right"
        >
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Icon sx={{ display: 'flex' }}>
              {
                TaskCardIconMap[
                  cellValues.row.status as UpdateChangeDtoStatusEnum
                ]
              }
            </Icon>
            <Typography>
              {
                TaskCardLocalization[
                  cellValues.row.status as UpdateChangeDtoStatusEnum
                ]
              }
            </Typography>
          </Box>
        </Tooltip>
      ),
    },
    {
      field: 'role',
      headerName: t('user.role'),
      width: 200,
      renderCell: cellValues => (
        <Typography>{t(cellValues.row.role).toString()}</Typography>
      ),
    },
    {
      field: 'implementationTargetDate',
      headerName: t('dates.implDate'),
      width: 100,
      renderCell: cellValues => (
        <Typography>
          {cellValues.row.implementationTargetDate
            ? new Date(
                cellValues.row.implementationTargetDate,
              ).toLocaleDateString('de-DE')
            : '-'}
        </Typography>
      ),
    },
    {
      field: 'evaluationTargetDate',
      headerName: t('change.evaluateUntil'),
      width: 140,
      renderCell: cellValues => (
        <Typography>
          {cellValues.row.evaluationTargetDate
            ? new Date(cellValues.row.evaluationTargetDate).toLocaleDateString(
                'de-DE',
              )
            : '-'}
        </Typography>
      ),
    },
    {
      field: 'submissionCRQuotationToClient',
      headerName: t('dates.externalApprovalCustomerDate'),
      width: 160,
      renderCell: cellValues => (
        <Typography
          sx={{
            color: cellValues.row.submissionCRQuotationToClient
              ? 'green'
              : undefined,
          }}
        >
          {cellValues.row.submissionCRQuotationToClient
            ? new Date(
                cellValues.row.submissionCRQuotationToClient,
              ).toLocaleDateString('de-DE')
            : '-'}
        </Typography>
      ),
    },
    {
      field: 'totalSalesValue',
      headerName: t('costs.totalSalesValue'),
      width: 150,
      renderCell: cellValues => (
        <Typography>
          {cellValues.row.totalSalesValue
            ? `€ ${formatNumber(cellValues.row.totalSalesValue)}`
            : '-'}
        </Typography>
      ),
    },
  ];

  return (
    <Box
      css={css`
        padding: 48px 24px 24px;
        flex-grow: 1;
        margin-left: auto;
        transition: width 225ms cubic-bezier(0, 0, 0.2, 1) 0ms;

        @media (max-width: 430px) {
          padding: 48px 16px 24px;
        }
      `}
    >
      <div style={{ height: 1000, width: '100%' }}>
        <DataGrid
          rows={changeRequests.map(entry => ({
            ...entry,
            statusText: t(entry.status),
          }))}
          columns={columns}
          paginationModel={{ pageSize: 100, page: 0 }}
          style={{ cursor: 'pointer', background: 'white' }}
          onRowClick={params => {
            const appendString = getChangeLinkByStatus(params.row.status);
            const url = `/change-request/${params.id}/${appendString}`;
            window.open(url, '_blank');
            navigation(`change-request/${params.id}`);
          }}
          slots={{
            toolbar: () => (
              <GridToolbarContainer sx={{ justifyContent: 'flex-end' }}>
                <GridToolbarQuickFilter sx={{ margin: 1 }} />
              </GridToolbarContainer>
            ),
          }}
          localeText={{
            toolbarQuickFilterLabel: t('buttons.search'),
            toolbarQuickFilterPlaceholder: t('buttons.search'),
          }}
        />
      </div>
    </Box>
  );
});

export default ChangeDataGrid;
